import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  IconButton,
  Divider,
  Grid,
  Chip,
  Skeleton,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

/* ================= UTILS (FIX IMPORTANT) ================= */
const isValid = (v) => {
  if (v === null || v === undefined) return false;

  if (typeof v === "string") {
    const clean = v.trim().toLowerCase();
    return clean !== "" && clean !== "null" && clean !== "undefined";
  }

  return true;
};

const safeText = (fr, en, lang) => {
  const value = lang === "FR" ? fr : en;

  if (!value) return "";

  if (typeof value === "string") {
    const clean = value.trim().toLowerCase();
    if (clean === "null" || clean === "undefined") return "";
  }

  return value;
};

/* ================= AUTO MAPPING ================= */
const buildSections = (recipe, t, lang) => [
  {
    key: "ingredients",
    icon: "🧂",
    title: t("ingredients"),
    value: safeText(recipe.ingredientFr, recipe.ingredientEn, lang),
  },
  {
    key: "recette",
    icon: "🍽",
    title: t("recipe"),
    value: safeText(recipe.recetteFr, recipe.recetteEn, lang),
  },
  {
    key: "portion",
    icon: "🍱",
    title: t("portion"),
    value: safeText(recipe.portionMinFr, recipe.portionMinEn, lang),
  },
  {
    key: "takeaway",
    icon: "🥡",
    title: t("takeAway"),
    value: safeText(recipe.emporterFr, recipe.emporterEn, lang),
  },
  {
    key: "decoration",
    icon: "🎨",
    title: t("decoration"),
    value: safeText(recipe.decorationFr, recipe.decorationEn, lang),
  },
  {
    key: "utensils",
    icon: "🍽",
    title: t("utensils"),
    value: safeText(recipe.ustensileFr, recipe.ustensileEn, lang),
  },
];

/* ================= MEDIA CAROUSEL ================= */
function MediaSection({ mainImage, gallery, hasVideo, id, lang }) {
  const images = [
    ...(mainImage ? [{ id: "main", imageUrl: mainImage, isMain: true }] : []),
    ...(gallery || []).map((g) => ({
      id: g.id,
      imageUrl: g.imageUrl,
      isMain: false,
    })),
  ];

  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    setIndex(0);
  }, [mainImage]);

  useEffect(() => {
    setShowVideo(false);
  }, [mainImage]);

  const current = images[index];

  const prev = () => setIndex((i) => (i === 0 ? images.length - 1 : i - 1));

  const next = () => setIndex((i) => (i === images.length - 1 ? 0 : i + 1));

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 50) next();
    if (diff < -50) prev();
  };
  const videoUrl = `${process.env.REACT_APP_API_URL}/ManageVideoRecipe/PlayStreamVideo/${id}?lang=${lang}`;

  return (
    <Box>
      {/* MAIN VIEW */}
      <Paper sx={mediaStyle}>
        {current ? (
          <Box
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            sx={{ position: "relative" }}
          >
            {showVideo && hasVideo && current.isMain ? (
              <video
                controls
                autoPlay
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
                onEnded={() => setShowVideo(false)}
              >
                <source src={videoUrl} type="video/mp4" />
              </video>
            ) : (
              <Box
                onClick={() => {
                  if (hasVideo && current.isMain) {
                    setShowVideo(true);
                  }
                }}
                sx={{
                  width: "100%",
                  height: {
                    xs: 260,
                    sm: 320,
                    md: 420,
                  },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: 4,
                  background: "#f5f7fa",
                  p: 2,
                  cursor: hasVideo && current.isMain ? "pointer" : "default",
                }}
              >
                <img
                  src={current.imageUrl}
                  alt=""
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: "16px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  }}
                />

                {hasVideo && current.isMain && (
                  <Box sx={playOverlay}>
                    <Box sx={playButton}>
                      <PlayArrowIcon sx={playIcon} />
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {current.isMain && (
              <Chip
                label="Main Image"
                color="primary"
                size="small"
                sx={chipStyle}
              />
            )}

            {images.length > 1 && (
              <>
                <IconButton onClick={prev} sx={navLeft}>
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>

                <IconButton onClick={next} sx={navRight}>
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        ) : (
          <Box sx={emptyBox}>No image</Box>
        )}
      </Paper>

      {/* THUMBNAILS (🔥 RESTORED) */}
      {images.length > 1 && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            mt: 1,
          }}
        >
          {images.map((img, i) => (
            <Box
              key={img.id || i}
              onClick={() => setIndex(i)}
              sx={{
                width: 70,
                height: 50,
                borderRadius: 2,
                overflow: "hidden",
                cursor: "pointer",
                border:
                  i === index
                    ? "2px solid #1976d2"
                    : img.isMain
                      ? "2px solid #ff9800"
                      : "1px solid #ddd",
                opacity: i === index ? 1 : 0.6,
                transition: "0.2s",
                position: "relative",
              }}
            >
              <img
                src={img.imageUrl}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />

              {/* small MAIN badge */}
              {img.isMain && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 2,
                    left: 2,
                    fontSize: 9,
                    px: 0.5,
                    borderRadius: 1,
                    background: "rgba(0,0,0,0.6)",
                    color: "#fff",
                  }}
                >
                  main
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/* ================= SECTION ================= */
function Section({ icon, title, children }) {
  return (
    <Paper sx={sectionStyle}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography fontSize={16} mr={1}>
          {icon}
        </Typography>
        <Typography fontSize={14} fontWeight={600}>
          {title}
        </Typography>
      </Box>

      <Divider sx={{ mb: 1 }} />

      <Typography fontSize={13} color="text.secondary">
        {children}
      </Typography>
    </Paper>
  );
}

/* ================= MAIN PAGE ================= */
function RecipeDetails() {
  const { t } = useTranslation(["Recettes"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userState = useSelector((state) => state.user);
  const categoryId = location.state?.categoryId;
  const lang = userState.lang || "FR";
  const [recipe, setRecipe] = useState(null);

  const [hasVideo, setHasVideo] = useState(false);
  const ADMIN_ROLES = ["RecipeAdmin", "AdminTI"];

  const hasAdminAccess = (roles = []) =>
    roles.some((role) => ADMIN_ROLES.includes(role));

  const canAdmin = hasAdminAccess(userState?.roles || []);

  const splitLines = (text) =>
    text ? text.split("\n").filter((t) => t.trim() !== "") : [];

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/ManageVideoRecipe/GetIfVideoExist/${id}/${lang}`,
        );

        if (!res.ok) {
          console.error("API error:", res.status);
          setHasVideo(false);
          return;
        }

        const data = await res.json();
        setHasVideo(data.exists);
      } catch (err) {
        console.error("Video check error", err);
        setHasVideo(false);
      }
    };

    if (id && lang) {
      fetchVideo();
    }
  }, [id, lang]);

  useEffect(() => {
    const fetchRecipe = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/RecipePreparation/GetRecipeById/${id}`,
        {
          headers: {
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      const data = await res.json();
      setRecipe(data);
    };

    fetchRecipe();
  }, [id, userState.token]);

  /* ================= LOADING ================= */
  if (!recipe) {
    return (
      <Box sx={{ maxWidth: 1000, mx: "auto", p: 2 }}>
        <Skeleton height={40} sx={{ mb: 2 }} />
        <Skeleton height={340} sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton height={120} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={pageStyle}>
      <Paper sx={containerStyle}>
        {/* HEADER */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>

          <Typography fontSize={20} fontWeight={700} ml={1}>
            {t("recipeDetails")}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* TITLE */}
        <Typography fontSize={24} fontWeight={700}>
          {safeText(recipe.nomFr, recipe.nomEn, lang)}
        </Typography>

        <Chip label={t("recipe")} size="small" sx={{ mt: 1, mb: 2 }} />

        {/* MEDIA */}
        <MediaSection
          mainImage={recipe.imageUrl}
          gallery={recipe.galleryImages}
          hasVideo={hasVideo}
          id={id}
          lang={lang}
        />

        {/* AUTO SECTIONS */}
        <Grid container spacing={1.5} mt={1}>
          {buildSections(recipe, t, lang)
            .filter((s) => isValid(s.value))
            .map((s) => (
              <Grid item xs={12} md={6} key={s.key}>
                <Section icon={s.icon} title={s.title}>
                  {[
                    "ingredients",
                    "recette",
                    "decoration",
                    "utensils",
                  ].includes(s.key)
                    ? splitLines(s.value).map((item, i) => (
                        <Typography key={i} fontSize={13}>
                          • {item}
                        </Typography>
                      ))
                    : s.value}
                </Section>
              </Grid>
            ))}
        </Grid>

        {/* ACTIONS */}
        <Stack direction="row" spacing={1.5} mt={3} justifyContent="flex-end">
          {canAdmin && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() =>
                navigate(`/Recipe/Edit/${id}`, {
                  state: { categoryId },
                })
              }
            >
              {t("edit")}
            </Button>
          )}

          <Button size="small" variant="contained" onClick={() => navigate(-1)}>
            {t("back")}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

/* ================= STYLES ================= */
const pageStyle = {
  minHeight: "100vh",
  background: "#f4f6f8",
  p: 2,
};

const containerStyle = {
  maxWidth: 1000,
  mx: "auto",
  p: 3,
  borderRadius: 4,
  background: "#fff",
  boxShadow: "0 8px 30px rgba(0,0,0,0.05)",
};

const sectionStyle = {
  p: 2,
  borderRadius: 3,
  background: "#fafafa",
  border: "1px solid #eee",
};

const mediaStyle = {
  borderRadius: 3,
  overflow: "hidden",
  position: "relative",
  mb: 1.5,
};

const imgStyle = {
  width: "100%",
  height: "340px",
  objectFit: "cover",
};
const mediaStyleFixed = {
  width: "100%",
  height: "360px",
  borderRadius: 12,
  overflow: "hidden",
  background: "#000",
};

const chipStyle = {
  position: "absolute",
  top: 10,
  left: 10,
};

const navLeft = {
  position: "absolute",
  top: "50%",
  left: 8,
  transform: "translateY(-50%)",
  background: "#fff",
};

const navRight = {
  position: "absolute",
  top: "50%",
  right: 8,
  transform: "translateY(-50%)",
  background: "#fff",
};

const emptyBox = {
  height: 340,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#eee",
};
const playOverlay = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(to top, rgba(0,0,0,0.35), rgba(0,0,0,0.05))",
  cursor: "pointer",
  transition: "0.3s",
  "&:hover": {
    background: "linear-gradient(to top, rgba(0,0,0,0.5), rgba(0,0,0,0.1))",
  },
};

const playButton = {
  width: 72,
  height: 72,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(255,255,255,0.15)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255,255,255,0.3)",
  transition: "0.25s",
  "&:hover": {
    transform: "scale(1.08)",
  },
};

const playIcon = {
  fontSize: 40,
  color: "#fff",
  marginLeft: "4px",
};

export default RecipeDetails;
